import React, { useState } from "react";
import { toast } from "react-toastify";
import InvoiceStatusBadge from "./invoiceStatusBadge.jsx";
import InvoiceActionDialog from "./invoiceActionDialog.jsx";
import { fetchClientDocumentSignedUrl } from "../../../utils/apiUtils";

const PAGE_SIZE_OPTIONS = [20, 50, 100];

// Files carry a raw S3 key (`s3Path`); fetch a short-lived signed URL at download time.
// Open the tab synchronously so the popup is tied to the click, then redirect it once signed.
const openSignedFile = async (file) => {
    if (!file) return;
    const popup = window.open("", "_blank");
    try {
        const { url } = await fetchClientDocumentSignedUrl(file.s3Path);
        if (popup) popup.location = url;
        else window.open(url, "_blank");
    } catch {
        popup?.close();
        toast.error("Could not open the file. Please try again.");
    }
};

const formatDate = (iso) => {
    if (!iso) return "—";
    return iso;
};

const SkeletonRow = () => (
    <tr className="invoice-review-table__skeleton-row">
        {Array.from({ length: 11 }).map((_, i) => (
            <td key={i}><span className="invoice-review-table__skeleton" /></td>
        ))}
    </tr>
);

const InvoiceReviewTable = ({
    items,
    isLoading,
    selectedIds,
    onToggleRow,
    onToggleAll,
    onAccept,
    onReject,
    pageSize,
    onPageSizeChange,
    nextToken,
    tokenStack,
    onNext,
    onPrevious,
}) => {
    const allSelected = items.length > 0 && selectedIds.size === items.length;
    const someSelected = selectedIds.size > 0 && !allSelected;

    const [dialogState, setDialogState] = useState(null);

    const openDialog = (invoice, action) => setDialogState({ invoice, action });
    const closeDialog = () => setDialogState(null);

    const handleDialogConfirm = () => {
        if (!dialogState) return;
        const { invoice, action } = dialogState;
        if (action === "accept") {
            onAccept(invoice);
        } else {
            onReject(invoice);
        }
        closeDialog();
    };

    return (
        <div className="invoice-review-table-wrap">
            <div className="table-wrapper">
                <table className="invoice-review-table">
                    <thead>
                        <tr>
                            <th>
                                <input
                                    type="checkbox"
                                    aria-label="Select all"
                                    checked={allSelected}
                                    ref={(el) => { if (el) el.indeterminate = someSelected; }}
                                    onChange={onToggleAll}
                                    disabled={isLoading || items.length === 0}
                                />
                            </th>
                            <th>Account Name</th>
                            <th>Account ID</th>
                            <th>Bill ID</th>
                            <th>Run Date</th>
                            <th>Period Start</th>
                            <th>Period End</th>
                            <th>Status</th>
                            <th>Invoices</th>
                            <th>Billing Report</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {isLoading && items.length === 0 ? (
                            Array.from({ length: 5 }).map((_, i) => <SkeletonRow key={i} />)
                        ) : items.length === 0 ? (
                            <tr>
                                <td colSpan={11} className="invoice-review-table__empty">
                                    No invoices found for the selected filters.
                                </td>
                            </tr>
                        ) : (
                            items.map((item) => {
                                const isChecked = selectedIds.has(item.sk);
                                const nonPhiFile = item.files?.find((f) => !f.phi);
                                const phiFile = item.files?.find((f) => f.phi);
                                const canAccept = item.status !== "accepted";
                                const canReject = item.status !== "accepted" && item.status !== "rejected";

                                return (
                                    <tr
                                        key={item.sk}
                                        className={`invoice-review-table__row${isChecked ? " invoice-review-table__row--selected" : ""}`}
                                    >
                                        <td className={item.status === "accepted" ? "opacity-50 cursor-not-allowed" : undefined}>
                                            <input
                                                type="checkbox"
                                                aria-label={`Select ${item.accountName}${item.billId ? ` bill ${item.billId}` : ""}`}
                                                checked={isChecked}
                                                onChange={() => onToggleRow(item.sk)}
                                                disabled={item.status === "accepted"}
                                                title={item.status === "accepted" ? "Accepted invoices cannot be modified" : undefined}
                                            />
                                        </td>
                                        <td>{item.accountName}</td>
                                        <td>{item.accountHccId}</td>
                                        <td>{item.billId ?? "—"}</td>
                                        <td>{formatDate(item.runDate)}</td>
                                        <td>{formatDate(item.periodStart)}</td>
                                        <td>{formatDate(item.periodEnd)}</td>
                                        <td>
                                            <InvoiceStatusBadge status={item.status} aria-label={`Status: ${item.status}`} />
                                        </td>
                                        <td>
                                            <button
                                                type="button"
                                                className={`invoice-review-table__download-btn${!nonPhiFile ? " opacity-50 cursor-not-allowed" : ""}`}
                                                disabled={!nonPhiFile}
                                                aria-label="Download Invoices file"
                                                title={!nonPhiFile ? "No Invoices file available" : undefined}
                                                onClick={() => openSignedFile(nonPhiFile)}
                                            >
                                                Invoices
                                            </button>
                                        </td>
                                        <td>
                                            <button
                                                type="button"
                                                className={`invoice-review-table__download-btn${!phiFile ? " opacity-50 cursor-not-allowed" : ""}`}
                                                disabled={!phiFile}
                                                aria-label="Download Billing Report file"
                                                title={!phiFile ? "No Billing Report file available" : undefined}
                                                onClick={() => openSignedFile(phiFile)}
                                            >
                                                Billing Report
                                            </button>
                                        </td>
                                        <td>
                                            {selectedIds.size === 0 && (
                                                <div className="invoice-review-table__actions-cell">
                                                    <button
                                                        type="button"
                                                        className={`invoice-review-table__action-btn invoice-review-table__action-btn--accept${!canAccept ? " opacity-50 cursor-not-allowed" : ""}`}
                                                        disabled={!canAccept}
                                                        aria-label="Accept invoice"
                                                        onClick={() => canAccept && openDialog(item, "accept")}
                                                    >
                                                        Accept
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className={`invoice-review-table__action-btn invoice-review-table__action-btn--reject${!canReject ? " opacity-50 cursor-not-allowed" : ""}`}
                                                        disabled={!canReject}
                                                        aria-label="Reject invoice"
                                                        onClick={() => canReject && openDialog(item, "reject")}
                                                    >
                                                        Reject
                                                    </button>
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>

            <div className="invoice-review-table__controls invoice-review-table__controls--bottom">
                <label className="invoice-review-table__page-size-label">
                    Rows per page:
                    <select
                        className="invoice-review-table__page-size"
                        value={pageSize}
                        onChange={(e) => onPageSizeChange(Number(e.target.value))}
                    >
                        {PAGE_SIZE_OPTIONS.map((n) => (
                            <option key={n} value={n}>{n}</option>
                        ))}
                    </select>
                </label>
                <div className="invoice-review-table__page-nav">
                    <button
                        type="button"
                        className="invoice-review-table__page-btn"
                        disabled={tokenStack.length === 0}
                        onClick={onPrevious}
                    >
                        Previous
                    </button>
                    <button
                        type="button"
                        className="invoice-review-table__page-btn"
                        disabled={!nextToken}
                        onClick={onNext}
                    >
                        Next
                    </button>
                </div>
            </div>

            {dialogState && (
                <InvoiceActionDialog
                    invoice={dialogState.invoice}
                    action={dialogState.action}
                    onConfirm={handleDialogConfirm}
                    onCancel={closeDialog}
                />
            )}
        </div>
    );
};

export default InvoiceReviewTable;
